/**
 * Admin form that creates a `customer_model_reports` entry (an approved MRE) and
 * uploads its supporting AVV and RAW PDFs to the `reports` storage bucket.
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth, supabase } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import AdminLayout from './AdminLayout';

// Prices are persisted as fixed 2-decimal strings to avoid float rounding drift.
const formatPriceForDb = (value) => {
  const numeric = Number(value ?? 0);
  if (Number.isNaN(numeric)) return NaN;
  return numeric.toFixed(2);
};

const AddNewCustomerModelReport = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    make: '',
    model: '',
    chassis_code: '',
    body_type: '',
    engine_option_1: '',
    engine_option_2: '-',
    transmission: '',
    drivetrain: '',
    seats_option_1: '',
    seats_option_2: '-',
    seat_option_3: '-',
    sevs_entry: '',
    welcab_type: 'NO',
    mr_approval: '',
    mr_wi_reference: '',
    mr_vc_reference: '',
    origin_requirements: '',
    cats_or_km_required: '',
    camper_conversion_required: '',
    sevs_expiry: '',
    start_date: '',
    end_date: '',
    gm_reference: '',
    cin_reference: '',
    mr_variant: '',
    vehicle_type: '',
    vehicle_price: '0',
    old_type: '',
    old_price: '0',
    mr_description: '',
  });

  // When checked, the default type and price fills the vehicle type and price.
  const [sameOldAsVehicle, setSameOldAsVehicle] = useState(true);

  // Add new state for files
  const [avvDocuments, setAvvDocuments] = useState([]);
  const [rawDocuments, setRawDocuments] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDocumentUploadPopup, setShowDocumentUploadPopup] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  // Add refs for file inputs
  const avvInputRef = React.useRef(null);
  const rawInputRef = React.useRef(null);

  useEffect(() => {
    if (!showSuccessPopup) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setShowSuccessPopup(false);
    }, 5000);

    return () => window.clearTimeout(timeoutId);
  }, [showSuccessPopup]);

  // Checks every field is filled (mr_description is optional) and price values are valid numbers.
  const validateForm = () => {
    const newErrors = {};
    const fields = Object.keys(formData);
    
    fields.forEach(field => {
      if (field === 'vehicle_price' || field === 'old_price') {
        if (formData[field] === '' || Number.isNaN(Number(formData[field]))) {
          newErrors[field] = field === 'vehicle_price'
            ? 'Vehicle price is required'
            : 'Old price is required';
        }
        return;
      }
      // Skip validation for mr_description (non-compulsory)
      if (field === 'mr_description') return;
      
      if (!formData[field] || formData[field].trim() === '') {
        newErrors[field] = `${field.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')} is required`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => {
      const next = { ...prev, [name]: value };

      if (sameOldAsVehicle && (name === 'vehicle_type' || name === 'vehicle_price')) {
        if (name === 'vehicle_type') {
          next.old_type = value;
        } else {
          next.old_price = value;
        }
      }

      return next;
    });

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSameOldAsVehicleChange = (e) => {
    const checked = e.target.checked;
    setSameOldAsVehicle(checked);

    if (checked) {
      setFormData(prev => ({
        ...prev,
        old_type: prev.vehicle_type,
        old_price: prev.vehicle_price,
      }));
    }
  };

  // Modify handleFileChange
  const handleFileChange = (e, type) => {
    const newFiles = Array.from(e.target.files);
    if (type === 'avv') {
      setAvvDocuments(prevFiles => [...prevFiles, ...newFiles]);
    } else {
      setRawDocuments(prevFiles => [...prevFiles, ...newFiles]);
    }
  };

  // Modify removeFile
  const removeFile = (fileName, type) => {
    if (type === 'avv') {
      setAvvDocuments(prevFiles => {
        const newFiles = prevFiles.filter(file => file.name !== fileName);
        // Update the input's files
        if (avvInputRef.current) {
          const dt = new DataTransfer();
          newFiles.forEach(file => dt.items.add(file));
          avvInputRef.current.files = dt.files;
        }
        return newFiles;
      });
    } else {
      setRawDocuments(prevFiles => {
        const newFiles = prevFiles.filter(file => file.name !== fileName);
        // Update the input's files
        if (rawInputRef.current) {
          const dt = new DataTransfer();
          newFiles.forEach(file => dt.items.add(file));
          rawInputRef.current.files = dt.files;
        }
        return newFiles;
      });
    }
  };

  // Uploads AVV and RAW PDFs into the reports bucket and returns the folder path used.
  // upsert is off so an existing file with the same name will fail instead of overwrite.
  const uploadFiles = async () => {
    const uploadPromises = [];
    
    try {
      // Generate a unique folder name based on make, model, and chassis code
      const folderName = `${formData.mr_approval} ${formData.make} ${formData.model} ${formData.chassis_code}`;

      // Upload AVV documents
      for (const file of avvDocuments) {
        const filePath = `${folderName}/AVV DOCUMENTS/${file.name}`;
        const uploadPromise = supabase.storage
          .from('reports') // your bucket name
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false // prevent overwriting existing files
          });
        uploadPromises.push(uploadPromise);
      }

      // Upload RAW documents
      for (const file of rawDocuments) {
        const filePath = `${folderName}/RAW DOCUMENTS/${file.name}`;
        const uploadPromise = supabase.storage
          .from('reports') // your bucket name
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false // prevent overwriting existing files
          });
        uploadPromises.push(uploadPromise);
      }

      // Wait for all uploads to complete
      const results = await Promise.all(uploadPromises);
      
      // Check for any upload errors
      const errors = results.filter(result => result.error);
      if (errors.length > 0) {
        throw new Error('Some files failed to upload');
      }

      return folderName; // Return the generated folder name
    } catch (error) {
      console.error('Error uploading files:', error);
      throw error;
    }
  };

  // Saves the new MRE row after uploading its documents. Both AVV and RAW files are required.
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!user) {
      setError('You must be logged in to upload model report files.');
      return;
    }

    const vehiclePriceRaw = Number(formData.vehicle_price);
    const oldPriceSource = sameOldAsVehicle ? formData.vehicle_price : formData.old_price;
    const oldPriceRaw = Number(oldPriceSource);

    if (formData.vehicle_price === '' || Number.isNaN(vehiclePriceRaw)) {
      setError('Vehicle price must be a valid number');
      return;
    }

    if (oldPriceSource === '' || Number.isNaN(oldPriceRaw)) {
      setError('Old price must be a valid number');
      return;
    }

    const vehiclePrice = formatPriceForDb(formData.vehicle_price);
    const oldPrice = formatPriceForDb(oldPriceSource);

    const submissionData = {
      ...formData,
      vehicle_price: vehiclePrice,
      old_type: sameOldAsVehicle ? formData.vehicle_type : formData.old_type,
      old_price: oldPrice,
    };

    if (!validateForm()) {
      setError('Please fill in all required fields');
      return;
    }

    if (avvDocuments.length === 0 || rawDocuments.length === 0) {
      setShowDocumentUploadPopup(true);
      return;
    }

    setLoading(true);

    try {
      // Upload files and get the folder name
      const folderName = await uploadFiles();

      // Insert new record with the calculated ID
      const { data, error: supabaseError } = await supabase
        .from('customer_model_reports')
        .insert([{
          ...submissionData,
          storage_path: folderName // Use the generated folder name
        }])
        .select();

      if (supabaseError) {
        console.error('Error inserting record:', supabaseError);
        throw supabaseError;
      }

      setFormData({
        make: '',
        model: '',
        chassis_code: '',
        body_type: '',
        engine_option_1: '',
        engine_option_2: '-',
        transmission: '',
        drivetrain: '',
        seats_option_1: '',
        seats_option_2: '-',
        seat_option_3: '-',
        sevs_entry: '',
        welcab_type: 'NO',
        mr_approval: '',
        mr_wi_reference: '',
        mr_vc_reference: '',
        origin_requirements: '',
        cats_or_km_required: '',
        camper_conversion_required: '',
        sevs_expiry: '',
        start_date: '',
        end_date: '',
        gm_reference: '',
        cin_reference: '',
        mr_variant: '',
        vehicle_type: 'Common',
        vehicle_price: '0',
        old_type: 'Common',
        old_price: '0',
        mr_description: '',
      });
      setSameOldAsVehicle(true);

      // Clear uploaded documents
      setAvvDocuments([]);
      setRawDocuments([]);
      
      setErrors({});

      setShowSuccessPopup(true);

    } catch (error) {
      console.error('Form submission failed:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to render form field with validation
  const renderFormField = (name, label, type = "text", options = null) => {
    if (options) {
      return (
        <div className="text-left">
          <label className="block text-sm font-medium text-[#333333] mb-1">
            {label}
            <span className="text-[#ff0000] ml-1">*</span>
          </label>
          <select
            name={name}
            value={formData[name]}
            onChange={handleChange}
            className={`mt-1 block w-full rounded-2xl border px-4 py-3 text-sm sm:text-base ${
              errors[name] ? 'border-[#ff0000]' : 'border-gray-300'
            } shadow-sm focus:border-[#ff0000] focus:ring focus:ring-[#ff0000] focus:ring-opacity-50 transition-colors duration-200`}
            required
          >
            <option value="">{`Select ${label}`}</option>
            {options.map((option) => (
              <option key={option.value || option} value={option.value || option}>
                {option.label || option}
              </option>
            ))}
          </select>
          {errors[name] && (
            <p className="mt-1 text-sm text-[#ff0000]">{errors[name]}</p>
          )}
        </div>
      );
    }
    return (
      <div className="text-left">
        <label className="block text-sm font-medium text-[#333333] mb-1">
          {label}
          <span className="text-[#ff0000] ml-1">*</span>
        </label>
        <input
          type={type}
          name={name}
          value={formData[name]}
          onChange={handleChange}
          step={name === 'vehicle_price' || name === 'old_price' ? '0.01' : undefined}
          placeholder={
            name === 'make' ? 'Enter make' :
            name === 'model' ? 'Enter model' :
            name === 'chassis_code' ? 'Enter chassis code' :
            name === 'body_type' ? 'Enter body type' :
            name === 'engine_option_1' ? 'Enter first engine option' :
            name === 'engine_option_2' ? 'Enter "-" if no second engine is applicable.' :
            name === 'transmission' ? 'Enter transmission' :
            name === 'drivetrain' ? 'Enter drivetrain' :
            name === 'seats_option_1' ? 'Enter first seat option' :
            name === 'seats_option_2' ? 'Enter "-" if no second seat is applicable.' :
            name === 'seat_option_3' ? 'Enter "-" if no third seat is applicable.' :
            name === 'sevs_entry' ? 'Eg. SEV-000759/SEV-000760' :
            name === 'welcab_type' ? 'Enter "NO" if not a welcab' :
            name === 'mr_approval' ? 'Enter MRE approval' :
            name === 'mr_wi_reference' ? 'Enter MR WI reference' :
            name === 'mr_vc_reference' ? 'Enter MR VC reference' :
            name === 'origin_requirements' ? 'Enter origin requirements' :
            name === 'cats_or_km_required' ? 'Enter cats or KM required' :
            name === 'camper_conversion_required' ? 'Enter camper conversion required' :
            name === 'gm_reference' ? 'Enter GM reference' :
            name === 'cin_reference' ? 'Enter CIN reference' :
            name === 'mr_variant' ? 'Enter MR variant' :
            name === 'start_date' ? 'Enter date ' :
            name === 'end_date' ? 'Enter date ' :
            name === 'sevs_expiry' ? 'Enter date ' : ''
          }
          className={`mt-1 block w-full rounded-2xl border px-4 py-3 text-sm sm:text-base ${
            errors[name] ? 'border-[#ff0000]' : 'border-gray-300'
          } shadow-sm focus:border-[#ff0000] focus:ring focus:ring-[#ff0000] focus:ring-opacity-50 transition-colors duration-200`}
          required
        />
        {errors[name] && (
          <p className="mt-1 text-sm text-[#ff0000]">{errors[name]}</p>
        )}
      </div>
    );
  };

  // Modify the renderFileUploadSection to show selected files
  const renderFileUploadSection = () => (
    <div className="col-span-2 space-y-6 mt-6">
      <div>
        <h3 className="text-md font-medium text-[#333333] mb-4 uppercase tracking-[0.04em]">Document Upload</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-[#333333] mb-1">
              AVV Documents
              <span className="text-[#ff0000] ml-1">*</span>
            </label>
            <input
              ref={avvInputRef}
              type="file"
              multiple
              onChange={(e) => handleFileChange(e, 'avv')}
              accept=".pdf"
              className="mt-1 block w-full rounded-2xl border border-gray-300 px-4 py-3 text-sm sm:text-base shadow-sm focus:border-[#ff0000] focus:ring-[#ff0000]"
            />
            {/* Display AVV files */}
            <div className="mt-2">
              {avvDocuments.map((file, index) => (
                <div key={index} className="flex items-center justify-between py-1">
                  <span className="text-sm text-gray-600">{file.name}</span>
                  <button
                    type="button"
                    onClick={() => removeFile(file.name, 'avv')}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full text-red-600 transition hover:bg-red-50 hover:text-red-800"
                    aria-label={`Remove ${file.name}`}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#333333] mb-1">
              RAW Documents
              <span className="text-[#ff0000] ml-1">*</span>
            </label>
            <input
              ref={rawInputRef}
              type="file"
              multiple
              onChange={(e) => handleFileChange(e, 'raw')}
              accept=".pdf"
              className="mt-1 block w-full rounded-2xl border border-gray-300 px-4 py-3 text-sm sm:text-base shadow-sm focus:border-[#ff0000] focus:ring-[#ff0000]"
            />
            {/* Display RAW files */}
            <div className="mt-2">
              {rawDocuments.map((file, index) => (
                <div key={index} className="flex items-center justify-between py-1">
                  <span className="text-sm text-gray-600">{file.name}</span>
                  <button
                    type="button"
                    onClick={() => removeFile(file.name, 'raw')}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full text-red-600 transition hover:bg-red-50 hover:text-red-800"
                    aria-label={`Remove ${file.name}`}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <AdminLayout
      activeItemId="add-model-report"
      title="New Model Report"
      subtitle="Create a new approved model report and upload the supporting AVV and RAW documents."
      contentClassName="mx-auto max-w-6xl"
      footerContainerClassName="mt-10 lg:mt-14"
    >
      {showSuccessPopup && (
        <div className="pointer-events-none fixed inset-x-0 top-6 z-50 flex justify-center px-4">
          <div className="pointer-events-auto w-full max-w-sm rounded-[1rem] border border-emerald-200 bg-white px-4 py-3 shadow-[0_18px_40px_-24px_rgba(15,23,42,0.35)] ring-1 ring-emerald-100/80">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M16.704 5.29a1 1 0 0 1 .006 1.414l-7.2 7.26a1 1 0 0 1-1.42.008L3.3 9.18a1 1 0 1 1 1.4-1.428l4.08 4.002 6.5-6.456a1 1 0 0 1 1.424-.008Z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900">Model report added successfully</h3>
                <p className="mt-1 text-xs text-slate-500">This confirmation will close automatically.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDocumentUploadPopup && createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/28 px-4 py-6 backdrop-blur-[3px]">
          <div className="w-full max-w-md rounded-lg bg-white p-6 text-center shadow-xl">
            <h3 className="text-lg font-semibold text-[#333333]">Document upload required</h3>
            <p className="mt-3 text-sm text-gray-600">Please upload both AVV and RAW documents.</p>
            <button
              type="button"
              onClick={() => setShowDocumentUploadPopup(false)}
              className="mt-6 rounded-md bg-[#ff0000] px-5 py-2 text-sm font-medium text-white shadow-sm hover:bg-[#cc0000] focus:outline-none focus:ring-2 focus:ring-[#ff0000] focus:ring-offset-2"
            >
              OK
            </button>
          </div>
        </div>,
        document.body
      )}

      <div className="w-full pb-12">
        <div className="mx-auto max-w-6xl rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm sm:p-6 lg:p-8">
          <div className="mb-8 flex flex-col gap-3 border-b border-slate-200 pb-6">
            <h2 className="text-md font-medium text-slate-900 uppercase tracking-[0.04em]">Create Model Report</h2>
            <p className="max-w-3xl text-sm text-slate-600 sm:text-base">
             Complete the information below to add a new model report. All fields are required.
            </p>
          </div>

          {error && (
            <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
              {renderFormField('make', 'Make')}
              {renderFormField('model', 'Model')}
              {renderFormField('chassis_code', 'Chassis Code')}
              {renderFormField('body_type', 'Body Type')}
              {renderFormField('engine_option_1', 'Engine Option 1')}
              {renderFormField('engine_option_2', 'Engine Option 2')}
              {renderFormField('transmission', 'Transmission')}
              {renderFormField('drivetrain', 'Drivetrain')}
              {renderFormField('seats_option_1', 'Seats Option 1')}
              {renderFormField('seats_option_2', 'Seats Option 2')}
              {renderFormField('seat_option_3', 'Seat Option 3')}
              <div className="text-left">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-[#333333]">
                    SEVS Entry
                    <span className="text-[#ff0000] ml-1">*</span>
                  </label>
                  <span className="group relative inline-flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5 cursor-help text-[#3555F2]">
                      <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm8.706-1.442c1.146-.573 2.437.463 2.126 1.706l-.709 2.836.042-.02a.75.75 0 0 1 .67 1.34l-.04.022c-1.147.573-2.438-.463-2.127-1.706l.71-2.836-.042.02a.75.75 0 1 1-.671-1.34l.041-.022ZM12 9a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z" clipRule="evenodd" />
                    </svg>
                    <span className="pointer-events-none absolute left-1/3 top-1/2 z-20 ml-3 hidden w-[min(10rem,calc(100vw-2rem))] -translate-y-1/2 rounded-md bg-gray-900 px-3 py-2 text-xs leading-5 text-white shadow-lg break-words whitespace-normal group-hover:block max-[640px]:left-0 max-[640px]:top-full max-[640px]:ml-0 max-[640px]:mt-2 max-[640px]:translate-x-0 max-[640px]:translate-y-0">
                      If you have multiple SEVS, add SEVS like SEV-000759/SEV-000760/SEV-000761 in this format.
                    </span>
                  </span>
                </div>
                <input
                  type="text"
                  name="sevs_entry"
                  value={formData.sevs_entry}
                  onChange={handleChange}
                  placeholder="Eg. SEV-000759/SEV-000760"
                  className={`mt-1 block w-full rounded-2xl border px-4 py-3 text-sm sm:text-base shadow-sm transition-colors duration-200 focus:border-[#ff0000] focus:ring focus:ring-[#ff0000] focus:ring-opacity-50 ${
                    errors.sevs_entry ? 'border-[#ff0000]' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.sevs_entry && (
                  <p className="mt-1 text-sm text-[#ff0000]">{errors.sevs_entry}</p>
                )}
              </div>
              {renderFormField('welcab_type', 'Welcab Type')}
              {renderFormField('mr_approval', 'MRE Approval')}
              {renderFormField('mr_wi_reference', 'MR WI Reference')}
              {renderFormField('mr_vc_reference', 'MR VC Reference')}
              {renderFormField('origin_requirements', 'Origin Requirements')}
              {renderFormField('cats_or_km_required', 'Cats or KM Required')}
              {renderFormField('camper_conversion_required', 'Camper Conversion Required')}
              {renderFormField('sevs_expiry', 'SEVS Expiry')}
              {renderFormField('start_date', 'Start Date')}
              {renderFormField('end_date', 'End Date')}
              {renderFormField('gm_reference', 'GM Reference')}
              {renderFormField('cin_reference', 'CIN Reference')}
              {renderFormField('mr_variant', 'MR Variant')}
              <div className="text-left">
                <label className="block text-sm font-medium text-[#333333] mb-1">MR Description</label>
                <input
                  type="text"
                  name="mr_description"
                  value={formData.mr_description}
                  onChange={handleChange}
                  placeholder="Enter MRE Description"
                  className="mt-1 block w-full rounded-2xl border border-gray-300 px-4 py-3 shadow-sm transition-colors duration-200 focus:border-[#ff0000] focus:ring focus:ring-[#ff0000] focus:ring-opacity-50"
                />
              </div>

              <div className="text-left">
                <label className="block text-sm font-medium text-[#333333] mb-1">
                  Vehicle Type
                  <span className="text-[#ff0000] ml-1">*</span>
                </label>
                <select
                  name="vehicle_type"
                  value={formData.vehicle_type}
                  onChange={handleChange}
                  className={`mt-1 block w-full rounded-2xl border px-4 py-3 text-sm sm:text-base shadow-sm transition-colors duration-200 focus:border-[#ff0000] focus:ring focus:ring-[#ff0000] focus:ring-opacity-50 ${
                    errors.vehicle_type ? 'border-[#ff0000]' : 'border-gray-300'
                  }`}
                  required
                >
                  <option value="">Select Vehicle Type</option>
                  <option value="Common">Common</option>
                  <option value="Fixed Price">Fixed Price</option>
                  <option value="Bikes">Bikes</option>
                  <option value="Unique">Unique</option>
                  <option value="Trucks">Trucks</option>
                  <option value="Welcab Ramp">Welcab Ramp</option>
                  <option value="Welcab Side Seat">Welcab Side Seat</option>
                  <option value="Campervan">Campervan</option>
                </select>
                {errors.vehicle_type && (
                  <p className="mt-1 text-sm text-[#ff0000]">{errors.vehicle_type}</p>
                )}
              </div>
              {renderFormField('vehicle_price', 'Vehicle Price', 'number')}

              <div className="col-span-1 sm:col-span-2 xl:col-span-3 text-left">
                <label className="inline-flex items-center gap-2 cursor-pointer rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={sameOldAsVehicle}
                    onChange={handleSameOldAsVehicleChange}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                  />
                  <span className="text-sm font-medium text-[#333333]">
                    Default type and price same as vehicle type and price
                  </span>
                </label>
              </div>

              <div className="text-left">
                <label className="block text-sm font-medium text-[#333333] mb-1">
                  Default Type
                  <span className="text-[#ff0000] ml-1">*</span>
                </label>
                <select
                  name="old_type"
                  value={formData.old_type}
                  onChange={handleChange}
                  disabled={sameOldAsVehicle}
                  className={`mt-1 block w-full rounded-2xl border px-4 py-3 text-sm sm:text-base shadow-sm transition-colors duration-200 focus:border-[#ff0000] focus:ring focus:ring-[#ff0000] focus:ring-opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100 ${
                    errors.old_type ? 'border-[#ff0000]' : 'border-gray-300'
                  }`}
                  required
                >
                  <option value="">Select Default Type</option>
                  <option value="Common">Common</option>
                  <option value="Fixed Price">Fixed Price</option>
                  <option value="Bikes">Bikes</option>
                  <option value="Unique">Unique</option>
                  <option value="Trucks">Trucks</option>
                  <option value="Welcab Ramp">Welcab Ramp</option>
                  <option value="Welcab Side Seat">Welcab Side Seat</option>
                  <option value="Campervan">Campervan</option>
                </select>
                {errors.old_type && (
                  <p className="mt-1 text-sm text-[#ff0000]">{errors.old_type}</p>
                )}
              </div>
              <div className="text-left">
                <label className="block text-sm font-medium text-[#333333] mb-1">
                  Default Price
                  <span className="text-[#ff0000] ml-1">*</span>
                </label>
                <input
                  type="number"
                  name="old_price"
                  value={formData.old_price}
                  onChange={handleChange}
                  step="0.01"
                  disabled={sameOldAsVehicle}
                  className={`mt-1 block w-full rounded-2xl border px-4 py-3 text-sm sm:text-base shadow-sm transition-colors duration-200 focus:border-[#ff0000] focus:ring focus:ring-[#ff0000] focus:ring-opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100 ${
                    errors.old_price ? 'border-[#ff0000]' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.old_price && (
                  <p className="mt-1 text-sm text-[#ff0000]">{errors.old_price}</p>
                )}
              </div>
            </div>

            {renderFileUploadSection()}

            <div className="flex flex-col justify-center gap-3 pt-2 sm:flex-row">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="rounded-2xl border border-slate-300 bg-white px-6 py-3 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#ff0000] focus:ring-offset-2"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`rounded-2xl border border-transparent bg-blue-600 px-6 py-3 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 ${
                  loading ? 'cursor-not-allowed opacity-50' : ''
                }`}
              >
                {loading ? 'Saving...' : 'Save Model Report'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AddNewCustomerModelReport; 
